import React from 'react';
import SEO from './SEO';
import JsonLd from './JsonLd';
import Breadcrumbs from './Breadcrumbs';
import AuthorBox from './AuthorBox';
import { FIRMA, ADRES_PELNY, CENNIK, cenaTekst, TERAPIA, DOJAZD } from '../data/firma';
import { TERAPIA_SERVICE, breadcrumbSchema, faqSchema } from '../data/schema';
import page from '../css/modules/ContentPage.module.css';
import s from '../css/modules/Landing.module.css';

/** Strona ofertowa terapii SI (spec sekcja 7). Wlasna, rozlaczna intencja
 *  wzgledem strony diagnozy - tu nie walczymy o fraze "diagnoza sensoryczna". */

const PATH = '/terapia-integracji-sensorycznej-olsztyn/';

const CRUMBS = [
  { name: 'Strona główna', path: '/' },
  { name: 'Terapia integracji sensorycznej Olsztyn', path: PATH },
];

const FAQ = [
  { q: 'Czy przed terapią konieczna jest diagnoza?', a: TERAPIA.czyDiagnoza },
  { q: 'Ile trwają zajęcia i jak często się odbywają?', a: `${TERAPIA.czasZajec} ${TERAPIA.czestotliwosc}` },
  { q: 'Jak sprawdzamy, czy terapia działa?', a: TERAPIA.postepy },
  { q: 'Czy rodzic bierze udział w zajęciach?', a: TERAPIA.rodzic },
];

export default function TerapiaOlsztyn() {
  return (
    <div className={page.page}>
      <SEO
        title="Terapia integracji sensorycznej Olsztyn | Sensorini"
        description="Terapia integracji sensorycznej dla dzieci w Olsztynie. Indywidualne zajęcia, zalecenia dla rodziców i wsparcie dopasowane do potrzeb dziecka."
        image="/image/terapia-sensoryczna-integracja-sensoryczna-sensorini-olsztyn.webp"
        pathname={PATH}
        type="website"
      />
      <JsonLd data={[TERAPIA_SERVICE, breadcrumbSchema(CRUMBS), faqSchema(FAQ)]} />

      <header className={page.hero}>
        <span className={`${page.blob} ${page.blobA}`} aria-hidden="true" />
        <span className={`${page.blob} ${page.blobB}`} aria-hidden="true" />
        <div className={page.heroInner}>
          <span className={page.eyebrow}>Gabinet Sensorini • Olsztyn</span>
          <h1 className={page.h1}>Terapia integracji sensorycznej dla dzieci w Olsztynie</h1>
          <p className={page.lead}>
            Zajęcia indywidualne prowadzone według planu ustalonego po diagnozie - tak, żeby dziecko
            ćwiczyło to, co faktycznie sprawia mu trudność, i robiło to z przyjemnością.
          </p>
          <div className={s.heroCtas}>
            <a href="#final-contact" className={page.btnPrimary} data-cta="booking" data-cta-location="hero">
              Umów diagnozę
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={page.btnGhost}>
              Zadzwoń: {FIRMA.telefon}
            </a>
          </div>
          <address className={s.heroAddress}>
            <strong>{FIRMA.nazwaKrotka}</strong>
            <br />
            {ADRES_PELNY}
          </address>
        </div>
      </header>

      <Breadcrumbs items={CRUMBS} />

      <div className={`${page.wrap} ${page.wrapWide}`}>
        <section className={page.section}>
          <h2>Dla kogo są te zajęcia?</h2>
          <p>{TERAPIA.dlaKogo}</p>
          <p>{TERAPIA.czyDiagnoza}</p>
          <p>
            Terapia integracji sensorycznej bywa elementem wsparcia dzieci między innymi z:
          </p>
          <ul className={page.chips}>
            <li>trudnościami w uczeniu się</li>
            <li>zaburzeniami ze spektrum autyzmu</li>
            <li>całościowymi zaburzeniami rozwoju</li>
            <li>opóźnieniem psychoruchowym</li>
            <li>chorobami genetycznymi</li>
          </ul>
          <p className={page.callout}>
            Nie prowadzę terapii „na objaw”. Jeśli diagnoza pokaże, że trudności mają inne podłoże,
            powiem o tym wprost i wskażę specjalistę, który pomoże skuteczniej.
          </p>
          <div className={s.ctaRow}>
            <p className={s.ctaRowText}>Zaczynamy od diagnozy - to ona wyznacza plan terapii.</p>
            <a href="/diagnoza-sensoryczna-olsztyn/" className={page.btnPrimary} data-cta="booking" data-cta-location="po-dla-kogo">
              Umów diagnozę
            </a>
          </div>
          <div style={{ margin: '1.4rem 0 0' }}>
            <a
              href="/kwestionariusz-sensomotoryczny/"
              className={page.btnDeep}
              data-cta="kwestionariusz"
              data-cta-location="po-dla-kogo"
            >
              Wypełnij kwestionariusz sensomotoryczny przed diagnozą Twojego dziecka
            </a>
            <p className={s.disclaimer} style={{ marginTop: '0.6rem' }}>
              Kwestionariusz wypełnia rodzic - zajmuje około 15 minut.
            </p>
          </div>
        </section>

        <section className={page.section}>
          <h2>Jak wyglądają zajęcia?</h2>
          <p>
            Zajęcia odbywają się w wyposażonej sali w Olsztynie i są dopasowane do wieku oraz
            możliwości dziecka. Dla dziecka wygląda to jak tor przeszkód i zabawa ruchowa, a nie jak
            ćwiczenia rehabilitacyjne. Wykorzystuję między innymi:
          </p>
          <ul className={page.list}>
            <li>huśtawki, platformy, hamaki i deskorolki terapeutyczne,</li>
            <li>materace, piłki rehabilitacyjne, równoważnie i tory przeszkód,</li>
            <li>zabawy dotykowe, masy plastyczne, szczotkowanie,</li>
            <li>ćwiczenia koordynacji wzrokowo-ruchowej i planowania ruchu,</li>
            <li>zadania wspierające koncentrację uwagi i organizację działania.</li>
          </ul>
        </section>

        <section className={page.section}>
          <h2>Jak powstaje plan terapii?</h2>
          <p>{TERAPIA.plan}</p>
          <ul className={page.list}>
            <li>{TERAPIA.czasZajec}</li>
            <li>{TERAPIA.czestotliwosc}</li>
            <li>{TERAPIA.postepy}</li>
            <li>{TERAPIA.rodzic}</li>
          </ul>
          <p className={page.callout}>{TERAPIA.zastrzezenie}</p>
        </section>

        <section className={page.section}>
          <h2>Kto prowadzi zajęcia?</h2>
          <AuthorBox />
        </section>

        <section className={page.section}>
          <h2>Ile kosztują zajęcia?</h2>
          <div className={s.priceBox}>
            <span className={s.priceTag}>{cenaTekst(CENNIK.terapia)}</span>
            <span className={s.priceUnit}>{CENNIK.terapia.jednostka}</span>
            <p style={{ marginTop: '1rem', marginBottom: 0 }}>
              <a href="/cennik/">Pełny cennik</a> zawiera także koszt diagnozy, konsultacji i zasady
              odwoływania wizyt.
            </p>
          </div>
        </section>

        <section className={page.section}>
          <h2>Najczęstsze pytania</h2>
          <div className={page.faqList}>
            {FAQ.map((f, i) => (
              <details className={page.faqItem} key={f.q}>
                <summary className={page.question}>
                  <span className={page.qIndex}>{String(i + 1).padStart(2, '0')}</span>
                  {f.q}
                </summary>
                <div className={page.answer}>
                  <p>{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className={page.section}>
          <h2>Gdzie odbywają się zajęcia?</h2>
          <p>
            Gabinet mieści się przy {FIRMA.ulica} w Olsztynie. {DOJAZD.dzielnica} {DOJAZD.parking}
          </p>
          <p>
            Szczegóły dojazdu, wejścia do budynku i dostępności znajdziesz na{' '}
            <a href="/kontakt/">stronie kontaktowej</a>.
          </p>
        </section>

        <p className={s.disclaimer}>
          Treść ma charakter edukacyjny i nie zastępuje indywidualnej diagnozy specjalistycznej.
        </p>
      </div>

      <section className={page.ctaBand} id="final-contact">
        <div className={page.ctaInner}>
          <h2>Chcesz rozpocząć terapię?</h2>
          <p>Pierwszym krokiem jest diagnoza. Zadzwoń albo napisz - ustalimy termin.</p>
          <div className={page.ctaBtns}>
            <a href="/diagnoza-sensoryczna-olsztyn/" className={page.btnPrimary} data-cta="booking" data-cta-location="final">
              Umów diagnozę
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={page.btnGhost}>
              Zadzwoń: {FIRMA.telefon}
            </a>
            <a href={`mailto:${FIRMA.email}`} className={page.btnGhost}>
              Napisz wiadomość
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
